import React, { type PropsWithChildren } from 'react';
import Button, {
	IconButton,
	type IconButtonProps,
	LinkIconButton,
	LinkButton,
} from '@atlaskit/button/new';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import ClockIcon from '@atlaskit/icon/core/clock';
import CloseIcon from '@atlaskit/icon/core/close';
import CommentIcon from '@atlaskit/icon/core/comment';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import PriorityBlockerIcon from '@atlaskit/icon/core/priority-blocker';
import PriorityTrivialIcon from '@atlaskit/icon/core/priority-trivial';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import ShieldIcon from '@atlaskit/icon/core/shield';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import WarningIcon from '@atlaskit/icon/core/warning';
import BitbucketPullrequestsIcon from '@atlaskit/icon/glyph/bitbucket/pullrequests';
import CommentIconLegacy from '@atlaskit/icon/glyph/comment';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import EditorRemoveEmojiIcon from '@atlaskit/icon/glyph/editor/remove-emoji';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import FeedbackIconLegacy from '@atlaskit/icon/glyph/feedback';
import LikeIcon from '@atlaskit/icon/glyph/like';
import OpenIconLegacy from '@atlaskit/icon/glyph/open';
import WarningIconLegacy from '@atlaskit/icon/glyph/warning';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { media, Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import AutodevIcon from '@atlassian/jira-autodev/src/common/assets/autodev-icon';
import { HoverTracker } from '@atlassian/jira-insights-common';
import { useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { FormatMessage } from '@atlassian/jira-shared-types';
import { PERISCOPE_ANALYTICS } from '../../constants';
import { TaskTypeCard } from '../card';
import messages from './messages';

type Props = {
	id: string;
	suggestionType: string;
	actionButtonTitle: string;
	showReactionsButton: boolean;
	onSnooze: () => void;
	onRemove: () => void;
	onReactionsButtonClick: () => void;
	onActionButtonClick?: () => void;
	url?: string;
	title?: string;
};

const getIcon = (suggestionType: string, formatMessage: FormatMessage) => {
	switch (suggestionType) {
		case 'WorkSuggestionsBuildTask':
		case 'WorkSuggestionsDeploymentTask':
			return (
				<Box backgroundColor="color.background.accent.red.subtler">
					<WarningIcon
						color={token('color.icon.accent.red')}
						spacing="spacious"
						LEGACY_fallbackIcon={WarningIconLegacy}
						LEGACY_secondaryColor={token('color.icon.inverse')}
						label={formatMessage(messages.failedBuildOrDeploymentLabelNonfinal, {
							suggestionType,
						})}
					/>
				</Box>
			);
		case 'WorkSuggestionsPullRequestReviewTask':
			return (
				<Box backgroundColor="color.background.accent.blue.subtler">
					<PullRequestIcon
						color={token('color.icon.accent.blue')}
						spacing="spacious"
						LEGACY_fallbackIcon={BitbucketPullrequestsIcon}
						label={formatMessage(messages.pullRequestIconLabelNonfinal)}
					/>
				</Box>
			);
		case 'WorkSuggestionsPullRequestNeedsWorkTask':
			return (
				<Box backgroundColor="color.background.accent.green.subtler">
					<PullRequestIcon
						color={token('color.icon.accent.green')}
						spacing="spacious"
						LEGACY_fallbackIcon={BitbucketPullrequestsIcon}
						label={formatMessage(messages.pullRequestIconLabelNonfinal)}
					/>
				</Box>
			);
		case 'WorkSuggestionsCodeTabPullRequestStaleTask':
			return (
				<Box backgroundColor="color.background.accent.yellow.subtler">
					<PriorityTrivialIcon
						color={token('color.icon.accent.yellow')}
						spacing="spacious"
						LEGACY_fallbackIcon={BitbucketPullrequestsIcon}
						label={formatMessage(messages.pullRequestIconLabelNonfinal)}
					/>
				</Box>
			);
		case 'WorkSuggestionsCriticalVulnerabilityTask':
			return (
				<Box backgroundColor="color.background.accent.purple.subtler">
					<ShieldIcon
						color={token('color.icon.accent.purple')}
						spacing="spacious"
						LEGACY_fallbackIcon={WarningIconLegacy}
						LEGACY_secondaryColor={token('color.icon.inverse')}
						label={formatMessage(messages.criticalVulnerabilityIconLabelNonfinal)}
					/>
				</Box>
			);
		case 'WorkSuggestionsBlockingIssueTask':
			return (
				<Box backgroundColor="color.background.accent.orange.subtler">
					<PriorityBlockerIcon
						color={token('color.icon.accent.orange')}
						spacing="spacious"
						LEGACY_fallbackIcon={EditorRemoveEmojiIcon}
						label={formatMessage(messages.blockingIssueIconLabelNonfinal)}
					/>
				</Box>
			);
		case 'WorkSuggestionsAutoDevJobsPlanSuccessTask':
			return <AutodevIcon size={24} />;
		case 'WorkSuggestionsPRCommentsTask':
			return (
				<Box backgroundColor="color.background.accent.lime.subtlest">
					<CommentIcon
						color={token('color.text.accent.lime')}
						spacing="spacious"
						LEGACY_fallbackIcon={CommentIconLegacy}
						label=""
					/>
				</Box>
			);
		case 'WorkSuggestionsCompassAnnouncementTask':
			return (
				<Box backgroundColor="color.background.accent.blue.subtlest">
					<MegaphoneIcon
						color={token('color.icon.brand')}
						spacing="spacious"
						LEGACY_fallbackIcon={FeedbackIconLegacy}
						label=""
					/>
				</Box>
			);
		default:
			return null;
	}
};

const getTitle = (suggestionType: string, formatMessage: FormatMessage) => {
	switch (suggestionType) {
		case 'WorkSuggestionsBuildTask':
			return formatMessage(messages.failedBuildTaskTitleNonfinal);
		case 'WorkSuggestionsDeploymentTask':
			return formatMessage(messages.failedDeploymentTaskTitleNonfinal);
		case 'WorkSuggestionsPullRequestReviewTask':
			return formatMessage(messages.pullRequestReviewTaskTitleNonfinal);
		case 'WorkSuggestionsPullRequestNeedsWorkTask':
			return formatMessage(messages.pullRequestNeedsWorkTaskTitleNonfinal);
		case 'WorkSuggestionsCodeTabPullRequestStaleTask':
			return formatMessage(messages.pullRequestInactiveTaskTitleNonfinal);
		case 'WorkSuggestionsCriticalVulnerabilityTask':
			return formatMessage(messages.criticalVulnerabilityTaskTitleNonfinal);
		case 'WorkSuggestionsBlockingIssueTask':
			return formatMessage(messages.blockingIssueTaskTitleNonfinal);
		case 'WorkSuggestionsAutoDevJobsPlanSuccessTask':
			return formatMessage(messages.autoDevJobsPlanSuccessTaskTitleNonfinal);
		case 'WorkSuggestionsCompassAnnouncementTask':
			return formatMessage(messages.compassAnnouncementTaskTitleNonfinal);
		default:
			return null;
	}
};

export const TaskTypeItem = ({
	children,
	id,
	suggestionType,
	actionButtonTitle,
	onSnooze,
	onRemove,
	url,
	showReactionsButton,
	onReactionsButtonClick,
	onActionButtonClick,
	title,
}: PropsWithChildren<Props>) => {
	const { formatMessage } = useIntl();

	const snoozeTooltipOptions: IconButtonProps['tooltip'] = {
		position: 'top',
		hideTooltipOnClick: true,
		content: formatMessage(messages.snoozeTooltipNonfinal),
	};

	const removeTooltipOptions: IconButtonProps['tooltip'] = {
		position: 'top',
		hideTooltipOnClick: true,
		content: formatMessage(messages.removeTooltipNonfinal),
	};

	const analyticsAttributes: Attributes = {
		suggestionId: id,
		suggestionType,
	};

	const getActionComponentFullText = () => {
		if (url) {
			return (
				<LinkButton
					href={url}
					iconBefore={(iconProps) => (
						// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
						<ArrowUpRightIcon LEGACY_fallbackIcon={OpenIconLegacy} {...iconProps} />
					)}
					target="_blank"
					rel="noreferrer"
					onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
						if (onActionButtonClick) {
							onActionButtonClick();
						}
						fireUIAnalytics(
							analyticsEvent,
							`${PERISCOPE_ANALYTICS}.workSuggestions.action`,
							analyticsAttributes,
						);
					}}
				>
					{actionButtonTitle}
				</LinkButton>
			);
		}
		if (onActionButtonClick) {
			return (
				<Button
					iconBefore={(iconProps) => (
						// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
						<ArrowUpRightIcon LEGACY_fallbackIcon={OpenIconLegacy} {...iconProps} />
					)}
					onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
						onActionButtonClick();
						fireUIAnalytics(
							analyticsEvent,
							`${PERISCOPE_ANALYTICS}.workSuggestions.action`,
							analyticsAttributes,
						);
					}}
				>
					{actionButtonTitle}
				</Button>
			);
		}
		return null;
	};

	const getActionComponentIconOnly = () => {
		if (url) {
			return (
				<LinkIconButton
					href={url}
					icon={(iconProps) => (
						// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
						<ArrowUpRightIcon LEGACY_fallbackIcon={OpenIconLegacy} {...iconProps} />
					)}
					onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
						if (onActionButtonClick) {
							onActionButtonClick();
						}
						fireUIAnalytics(
							analyticsEvent,
							`${PERISCOPE_ANALYTICS}.workSuggestions.action`,
							analyticsAttributes,
						);
					}}
					label={formatMessage(messages.linkIconLabelNonfinal)}
					target="_blank"
					rel="noreferrer"
				/>
			);
		}
		if (onActionButtonClick) {
			return (
				<IconButton
					icon={(iconProps) => (
						// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
						<ArrowUpRightIcon LEGACY_fallbackIcon={OpenIconLegacy} {...iconProps} />
					)}
					onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
						onActionButtonClick();
						fireUIAnalytics(
							analyticsEvent,
							`${PERISCOPE_ANALYTICS}.workSuggestions.action`,
							analyticsAttributes,
						);
					}}
					label={actionButtonTitle}
				/>
			);
		}
		return null;
	};

	return (
		<TaskTypeCard>
			<HoverTracker
				minTime={1000}
				eventAnalytics={{
					actionSubjectId: `${PERISCOPE_ANALYTICS}.workSuggestions.suggestionCardHover`,
				}}
			>
				<Flex direction="column" gap="space.150">
					<Flex gap="space.100" alignItems="center">
						<Box xcss={iconWrapperStyles}>{getIcon(suggestionType, formatMessage)}</Box>
						<Box xcss={titleStyles}>{title || getTitle(suggestionType, formatMessage)}</Box>

						<Flex xcss={buttonGroupFlexStyles} gap="space.050" justifyContent="end">
							<Show above="md">
								<Tooltip content={actionButtonTitle} position="top">
									{getActionComponentFullText()}
								</Tooltip>
							</Show>
							<Show below="md" xcss={centerButtonStyles}>
								<Flex alignItems="center">
									<Tooltip content={actionButtonTitle} position="top">
										{getActionComponentIconOnly()}
									</Tooltip>
								</Flex>
							</Show>

							<IconButton
								icon={(iconProps) => (
									// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
									<ClockIcon LEGACY_fallbackIcon={EmojiFrequentIcon} {...iconProps} />
								)}
								label={formatMessage(messages.snoozeLabelNonfinal)}
								appearance="subtle"
								isTooltipDisabled={false}
								tooltip={snoozeTooltipOptions}
								onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
									onSnooze();
									fireUIAnalytics(
										analyticsEvent,
										`${PERISCOPE_ANALYTICS}.workSuggestions.snooze`,
										analyticsAttributes,
									);
								}}
							/>
							<IconButton
								// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
								icon={(iconProps) => <CloseIcon LEGACY_fallbackIcon={CrossIcon} {...iconProps} />}
								label={formatMessage(messages.removeLabelNonfinal)}
								appearance="subtle"
								isTooltipDisabled={false}
								tooltip={removeTooltipOptions}
								onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
									onRemove();
									fireUIAnalytics(
										analyticsEvent,
										`${PERISCOPE_ANALYTICS}.workSuggestions.remove`,
										analyticsAttributes,
									);
								}}
							/>
						</Flex>
					</Flex>
					{children}
					{showReactionsButton && (
						<Flex xcss={footerStyles} alignItems="center" gap="space.050" justifyContent="start">
							<IconButton
								icon={(iconProps) => (
									// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
									<ThumbsUpIcon spacing="spacious" LEGACY_fallbackIcon={LikeIcon} {...iconProps} />
								)}
								label={formatMessage(messages.likeLabelNonfinal)}
								appearance="subtle"
								onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
									onReactionsButtonClick();
									fireUIAnalytics(
										analyticsEvent,
										`${PERISCOPE_ANALYTICS}.workSuggestions.userSentiment`,
										{ ...analyticsAttributes, thumbReaction: 'positive' },
									);
								}}
							/>
							<Box xcss={dislikeStyles}>
								<IconButton
									icon={(iconProps) => (
										// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
										<ThumbsDownIcon
											spacing="spacious"
											LEGACY_fallbackIcon={LikeIcon}
											{...iconProps}
										/>
									)}
									label={formatMessage(messages.dislikeLabelNonfinal)}
									appearance="subtle"
									onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
										onReactionsButtonClick();
										fireUIAnalytics(
											analyticsEvent,
											`${PERISCOPE_ANALYTICS}.workSuggestions.userSentiment`,
											{ ...analyticsAttributes, thumbReaction: 'negative' },
										);
									}}
								/>
							</Box>
						</Flex>
					)}
				</Flex>
			</HoverTracker>
		</TaskTypeCard>
	);
};

const dislikeStyles = xcss({
	transform: 'rotate(180deg)',
});

const footerStyles = xcss({
	fontSize: 'small',
	color: 'color.text.subtle',
});

const iconWrapperStyles = xcss({
	width: '24px',
	height: '24px',
	borderRadius: token('border.radius', '3px'),
	overflow: 'hidden',
});

const titleStyles = xcss({
	lineHeight: '16px',
	font: token('font.heading.xsmall'),
});

const centerButtonStyles = xcss({
	// @ts-expect-error doesn't support media.below yet
	[media.below.md]: {
		display: 'flex',
	},
});

const buttonGroupFlexStyles = xcss({
	flex: '1 1 0',
});
